import { create } from "zustand";
import { persist } from "zustand/middleware";

type Theme = "light" | "dark";

interface ThemeStore {
    theme: Theme;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
}

const STORAGE_KEY = "maeiz-rider-theme";

function applyThemeClass(theme: Theme) {
    if (typeof document === "undefined") return;
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
}

const getInitialTheme = (): Theme => {
    if (typeof window !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                return parsed?.state?.theme || "light";
            } catch {
                return "light";
            }
        }
    }
    return "light";
};

export const useThemeStore = create<ThemeStore>()(
    persist(
        (set) => ({
            theme: getInitialTheme(),
            setTheme: (theme) => {
                set({ theme });
                applyThemeClass(theme);
            },
            toggleTheme: () => {
                set((state) => {
                    const newTheme = state.theme === "dark" ? "light" : "dark";
                    applyThemeClass(newTheme);
                    return { theme: newTheme };
                });
            },
        }),
        {
            name: STORAGE_KEY,
            storage: {
                getItem: (name) => {
                    const str = localStorage.getItem(name);
                    return str ? JSON.parse(str) : null;
                },
                setItem: (name, value) => {
                    localStorage.setItem(name, JSON.stringify(value));
                },
                removeItem: (name) => {
                    localStorage.removeItem(name);
                },
            },
        }
    )
);
